import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import LocalLink from "@/components/LocalLink";
import { useIsMobile } from "@/hooks/use-mobile";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "@/assets/logo-event-boutique.webp";
import { useHeaderTheme } from "@/hooks/useHeaderTheme";
import { useLocale } from "@/lib/i18n/useLocale";
import { localise, twinFor, entryForPath, type Locale } from "@/lib/i18n/registry";

/**
 * Навигация одна на оба языка (BTQ-47, Этап 1).
 *
 * Пункт меню — это ключ в реестре пар плюс лейбл на каждом языке; адрес
 * вычисляется из реестра по локали текущей страницы. Поэтому португальское меню
 * совпадает с английским пункт в пункт. Версия v1 держала отдельный PtHeader с
 * четырьмя ссылками, и посетитель проваливался из сайта в огрызок — здесь это
 * невозможно по устройству: меню одно, разные у него только подписи и адреса.
 *
 * Порядок пунктов пока общий. Португальский спрос по гео идёт в другом порядке
 * (Algarve → Porto → Lisboa, см. pt-keyword-map-ads.md) — это решение волны B,
 * а не архитектуры.
 */
type DropdownId = "services" | "venues" | "locations";

interface NavItem {
  /** Ключ в реестре пар: из него берётся адрес на нужном языке. */
  key: string;
  label: Record<Locale, string>;
  dropdown?: DropdownId;
}

const navLinks: NavItem[] = [
  { key: "about", label: { en: "About", pt: "Sobre nós" } },
  { key: "services", label: { en: "Services", pt: "Serviços" }, dropdown: "services" },
  { key: "portfolio", label: { en: "Portfolio", pt: "Portfólio" } },
  { key: "venues", label: { en: "Venues", pt: "Quintas" }, dropdown: "venues" },
  { key: "locations", label: { en: "Locations", pt: "Regiões" }, dropdown: "locations" },
  { key: "blog", label: { en: "Blog", pt: "Blog" } },
  { key: "contact", label: { en: "Contact", pt: "Contactos" } },
];

const locationSubLinks: NavItem[] = [
  { key: "locations", label: { en: "All Locations", pt: "Todas as regiões" } },
  { key: "location-lisbon", label: { en: "Lisbon", pt: "Lisboa" } },
  { key: "location-cascais", label: { en: "Cascais", pt: "Cascais" } },
  { key: "location-sintra", label: { en: "Sintra", pt: "Sintra" } },
  { key: "location-comporta", label: { en: "Comporta", pt: "Comporta" } },
  { key: "location-algarve", label: { en: "Algarve", pt: "Algarve" } },
  { key: "location-douro-valley", label: { en: "Douro Valley", pt: "Douro" } },
  { key: "location-porto", label: { en: "Porto", pt: "Porto" } },
  { key: "location-hidden-gems", label: { en: "Hidden Gems", pt: "Lugares secretos" } },
];

const serviceSubLinks: NavItem[] = [
  { key: "services", label: { en: "All Services", pt: "Todos os serviços" } },
  { key: "service-wedding-planning", label: { en: "Wedding Planning", pt: "Organização de casamentos" } },
  { key: "service-creative-direction", label: { en: "Creative Direction", pt: "Direção criativa" } },
  { key: "service-event-production", label: { en: "Event Production", pt: "Produção de eventos" } },
  { key: "service-wedding-coordination", label: { en: "Wedding Coordination", pt: "Coordenação de casamentos" } },
  { key: "service-private-concierge", label: { en: "Private Concierge", pt: "Concierge privado" } },
];

const venueSubLinks: NavItem[] = [
  { key: "venues", label: { en: "All Venues", pt: "Todas as quintas" } },
  { key: "venue-palacio-da-bolsa", label: { en: "Palácio da Bolsa", pt: "Palácio da Bolsa" } },
  { key: "venue-the-yeatman", label: { en: "The Yeatman", pt: "The Yeatman" } },
  { key: "venue-six-senses-douro-valley", label: { en: "Six Senses Douro Valley", pt: "Six Senses Douro Valley" } },
  { key: "venue-vidago-palace", label: { en: "Vidago Palace", pt: "Vidago Palace" } },
  { key: "venue-parques-de-sintra", label: { en: "Parques de Sintra", pt: "Parques de Sintra" } },
  { key: "venue-palacio-de-seteais", label: { en: "Palácio de Seteais", pt: "Palácio de Seteais" } },
  { key: "venue-palacio-de-queluz", label: { en: "Palácio de Queluz", pt: "Palácio de Queluz" } },
  { key: "venue-penha-longa-resort", label: { en: "Penha Longa Resort", pt: "Penha Longa Resort" } },
  { key: "venue-quinta-da-bella-vista", label: { en: "Quinta da Bella Vista", pt: "Quinta da Bella Vista" } },
  { key: "venue-pestana-palace", label: { en: "Pestana Palace", pt: "Pestana Palace" } },
  { key: "venue-casa-sacoto", label: { en: "Casa Sacoto", pt: "Casa Sacoto" } },
  { key: "venue-sao-lourenco-do-barrocal", label: { en: "São Lourenço do Barrocal", pt: "São Lourenço do Barrocal" } },
  { key: "venue-belmond-reids-palace", label: { en: "Belmond Reid's Palace", pt: "Belmond Reid's Palace" } },
];

const dropdownMap: Record<DropdownId, NavItem[]> = {
  venues: venueSubLinks,
  locations: locationSubLinks,
  services: serviceSubLinks,
};

/** Подписи переключателя: каждая на языке той версии, куда он ведёт. */
const LANGUAGE_SWITCH: Record<Locale, { label: string; aria: string }> = {
  en: { label: "EN", aria: "View this page in English" },
  pt: { label: "PT", aria: "Ver esta página em português" },
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileHidden, setMobileHidden] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const [desktopDropdown, setDesktopDropdown] = useState<string | null>(null);
  const desktopDropdownRef = useRef<HTMLDivElement>(null);
  const desktopTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastScrollY = useRef(0);
  const location = useLocation();
  const navAria = (href: string): "page" | undefined => {
    const strip = (s: string) => (s !== "/" && s.endsWith("/") ? s.slice(0, -1) : s);
    // Родство пунктов меню считается по АНГЛИЙСКИМ адресам, а не по текущим.
    // Английские иерархичны (/services/creative-direction/), португальские слаги
    // плоские (/pt/direcao-criativa/) — они взяты из языка спроса, а не
    // транслитерированы. Поэтому правило «родитель — префикс потомка» на
    // португальской стороне не совпадает никогда, и подсветка родительского
    // пункта пропадала: расхождение пары в aria-current (BTQ-47, A3 партия 2).
    // На английской стороне отображение тождественно, вывод не меняется.
    const toEn = (path: string) => entryForPath(path)?.en ?? path;
    const p = strip(toEn(location.pathname));
    const h = strip(toEn(href));
    return p === h || (h !== "/" && p.startsWith(h + "/")) ? "page" : undefined;
  };
  const isMobile = useIsMobile();
  const locale = useLocale();
  /** Двойник текущей страницы на другом языке; null — когда его ещё нет. */
  const otherLocale: Locale = locale === "pt" ? "en" : "pt";
  const twinHref = twinFor(location.pathname, otherLocale);

  const sectionTheme = useHeaderTheme();

  const closeMenu = useCallback(() => {
    setMenuVisible(false);
    setTimeout(() => {
      setMenuOpen(false);
      setMobileExpanded(null);
    }, 350);
  }, []);

  const openMenu = useCallback(() => {
    setMenuOpen(true);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setMenuVisible(true));
    });
  }, []);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // Clamp: iOS rubber-band overscroll reports negative scrollY at the top
        // (and past-max values at the bottom), which flipped hide/show rapidly.
        const y = Math.max(0, window.scrollY);
        setScrolled(y > 10);
        const delta = y - lastScrollY.current;
        // Hysteresis: hide needs a firmer downward pull (20px, below 120px never
        // hide); show on a firm upward pull OR near the top. Symmetric 15px
        // thresholds made the header shuttle in/out on momentum-scroll wobble.
        if (delta > 20 && y > 120) {
          setMobileHidden(true);
          lastScrollY.current = y;
        } else if (delta < -20 || y < 64) {
          setMobileHidden(false);
          lastScrollY.current = y;
        }
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (menuOpen) closeMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const handleDesktopEnter = (label: string) => {
    clearTimeout(desktopTimeoutRef.current);
    setDesktopDropdown(label);
  };
  const handleDesktopLeave = () => {
    desktopTimeoutRef.current = setTimeout(() => setDesktopDropdown(null), 150);
  };

  // Hero transparent only when at top of page (not scrolled)
  const isHero = sectionTheme === "hero" && !scrolled && !menuOpen;
  const isOverDark = (sectionTheme === "dark" || (sectionTheme === "hero" && scrolled)) && !menuOpen;

  // Mobile: header background is ALWAYS transparent (never the cream/dark
  // glass surface). Only the ink contrasts with the section behind it.
  const mobileTransparent = isMobile && !menuOpen;
  // Is the section directly behind the header dark? (dark block or hero image)
  const sectionIsDark = sectionTheme === "dark" || sectionTheme === "hero";

  const headerBg = menuOpen
    ? "bg-background/95"
    : mobileTransparent
      ? "bg-transparent"
      : isHero
        ? "bg-transparent"
        : isOverDark
          ? "bg-background/90"
          : "bg-foreground/90";

  const textColor = menuOpen
    ? "text-foreground"
    : mobileTransparent
      ? sectionIsDark
        ? "text-white"
        : "text-foreground"
      : isHero
        ? "text-white"
        : isOverDark
          ? "text-foreground"
          : "text-white";

  const logoInvert = menuOpen
    ? ""
    : mobileTransparent
      ? sectionIsDark
        ? "brightness-0 invert"
        : ""
      : isHero
        ? "brightness-0 invert"
        : isOverDark
          ? ""
          : "brightness-0 invert";

  const noBlur = isHero || mobileTransparent;
  // Top scrim only when the header ink is white over a hero / dark image (not when the header has its own light bar, and not while the mobile header is hidden).
  const showScrim = !menuOpen && !(isMobile && mobileHidden) && (isHero || (mobileTransparent && sectionIsDark));

  // Dynamic theme-color for iOS status bar
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    let color: string;
    if (menuOpen) {
      color = "#ffffff";
    } else if (mobileTransparent || isHero) {
      color = "transparent";
    } else if (isOverDark) {
      color = "#ffffff";
    } else {
      color = "#1a1a1a";
    }
    meta.setAttribute("content", color);
  }, [menuOpen, isHero, isOverDark, mobileTransparent]);

  return (
    <>
      {/* Top scrim - gradient darkening so the white header reads over hero / dark imagery */}
      <div
        className="fixed top-0 left-0 right-0 z-40 pointer-events-none transition-opacity duration-500 ease-out"
        aria-hidden
        style={{
          height: "150px",
          background: "linear-gradient(180deg, rgba(20,15,12,0.5) 0%, rgba(20,15,12,0.22) 52%, transparent 100%)",
          opacity: showScrim ? 1 : 0,
        }}
      />
      <header
        className={`fixed top-0 left-0 right-0 z-50 ${headerBg} ${noBlur ? "" : "backdrop-blur-md"}`}
        style={{
          paddingTop: "env(safe-area-inset-top)",
          willChange: "transform, opacity",
          transition: "background-color 300ms ease, color 300ms ease, transform 400ms ease-in-out, opacity 400ms ease-in-out",
          ...(isMobile && !menuOpen && mobileHidden
            ? { opacity: 0, transform: "translateY(-100%)" }
            : { opacity: 1, transform: "translateY(0)" }),
        }}
      >
        <div className="luxury-container flex items-center justify-between h-20 md:h-[72px] lg:h-24">
          {/* Логотип ведёт на главную СВОЕЙ локали: с португальской страницы
              он уводил на английскую главную (BTQ-47). */}
          <a href={localise("home", locale).href ?? "/"} className="relative z-[60]">
            <img
              src={logo}
              alt="Event Boutique – Luxury Wedding Planner in Portugal"
              width={109}
              height={61}
              className={`h-10 md:h-12 w-auto transition-[filter] duration-300 ease ${logoInvert}`} decoding="async" />
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-9" aria-label="Main navigation">
            {navLinks.map((l) => {
              const { href, fellBack } = localise(l.key, locale);
              // Вести некуда: своей португальской страницы нет и замены тоже.
              // Пункт не рисуется — это честнее ссылки, уводящей с сайта.
              if (!href) return null;
              const label = l.label[locale];
              return l.dropdown ? (
                <div
                  key={l.key}
                  className="relative"
                  onMouseEnter={() => handleDesktopEnter(l.dropdown as string)}
                  onMouseLeave={handleDesktopLeave}
                >
                  <LocalLink
                    to={href}
                    data-i18n-substitute={fellBack ? "" : undefined}
                    aria-current={fellBack ? undefined : navAria(href)}
                    data-open={desktopDropdown === l.dropdown ? "true" : undefined}
                    className={`text-caption nav-cap ${textColor} transition-colors duration-300 ease inline-flex items-baseline gap-1`}
                  >
                    {label}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-300 ${desktopDropdown === l.dropdown ? "rotate-180" : ""}`}
                    />
                  </LocalLink>
                  <div
                    className="absolute top-full left-1/2 -translate-x-1/2 pt-4"
                    style={{
                      opacity: desktopDropdown === l.dropdown ? 1 : 0,
                      transform: desktopDropdown === l.dropdown ? "translateY(0)" : "translateY(8px)",
                      transition: "opacity 0.2s ease-out, transform 0.2s ease-out",
                      pointerEvents: desktopDropdown === l.dropdown ? "auto" : "none",
                    }}
                  >
                    <div className="nav-dropdown-panel py-2 px-2.5 min-w-[224px] max-h-[78vh] overflow-y-auto">
                      {dropdownMap[l.dropdown].map((sub) => {
                        const target = localise(sub.key, locale);
                        if (!target.href) return null;
                        return (
                          <LocalLink
                            key={sub.key}
                            to={target.href}
                            data-i18n-substitute={target.fellBack ? "" : undefined}
                            aria-current={target.fellBack ? undefined : navAria(target.href)}
                            className="nav-dropdown-item"
                          >
                            {sub.label[locale]}
                          </LocalLink>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <LocalLink
                  key={l.key}
                  to={href}
                  data-i18n-substitute={fellBack ? "" : undefined}
                  aria-current={fellBack ? undefined : navAria(href)}
                  className={`text-caption nav-cap ${textColor} transition-colors duration-300 ease`}
                >
                  {label}
                </LocalLink>
              );
            })}

            {/* Переключатель языка. Рисуется только когда у страницы есть
                готовый двойник: увести человека с его страницы на чужую главную
                — та же потеря, что и 404. */}
            {twinHref && (
              <Link
                to={twinHref}
                hrefLang={otherLocale === "pt" ? "pt-PT" : "en"}
                lang={otherLocale === "pt" ? "pt-PT" : "en"}
                data-language-switch={otherLocale}
                aria-label={LANGUAGE_SWITCH[otherLocale].aria}
                className={`text-caption nav-cap ${textColor} transition-colors duration-300 ease opacity-70 hover:opacity-100`}
              >
                {LANGUAGE_SWITCH[otherLocale].label}
              </Link>
            )}
          </nav>

          {/* Mobile Toggle */}
          <button
            onClick={() => (menuOpen ? closeMenu() : openMenu())}
            className={`lg:hidden relative z-[60] transition-colors duration-500 ${menuOpen ? "text-foreground" : textColor}`}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Full-Screen Overlay — CSS transitions only */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[55] bg-background"
          aria-hidden={!menuOpen}
          style={{
            opacity: menuVisible ? 1 : 0,
            transition: "opacity 0.35s ease-in-out",
          }}
        >
          <button
            onClick={closeMenu}
            className="absolute top-7 right-6 z-[60] text-foreground hover:opacity-60 transition-opacity"
            aria-label="Close menu"
          >
            <X size={28} strokeWidth={1.5} />
          </button>

          <nav className="h-full flex flex-col justify-center px-10" aria-label="Mobile navigation">
            <ul className="flex flex-col gap-2">
              {navLinks.map((l, i) => {
                const { href, fellBack } = localise(l.key, locale);
                if (!href) return null;
                const label = l.label[locale];
                const reveal = {
                  opacity: menuVisible ? 1 : 0,
                  transform: menuVisible ? "translateY(0)" : "translateY(20px)",
                  transition: `opacity 0.4s ease-out ${0.08 + i * 0.05}s, transform 0.4s ease-out ${0.08 + i * 0.05}s`,
                } as const;
                return l.dropdown ? (
                  <li key={l.key} style={reveal}>
                    <button
                      onClick={() => setMobileExpanded(mobileExpanded === l.dropdown ? null : (l.dropdown as string))}
                      className="w-full flex items-center justify-between py-3 text-foreground text-2xl md:text-3xl font-light tracking-wide"
                      aria-expanded={mobileExpanded === l.dropdown}
                    >
                      <span>{label}</span>
                      <ChevronDown
                        size={20}
                        strokeWidth={1.5}
                        className={`text-muted-foreground transition-transform duration-300 ${
                          mobileExpanded === l.dropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    <ul
                      className="overflow-hidden pl-6 flex flex-col"
                      style={{
                        maxHeight: mobileExpanded === l.dropdown ? "860px" : "0",
                        opacity: mobileExpanded === l.dropdown ? 1 : 0,
                        transition: "max-height 0.3s ease-in-out, opacity 0.3s ease-in-out",
                      }}
                    >
                      {dropdownMap[l.dropdown].map((sub) => {
                        const target = localise(sub.key, locale);
                        if (!target.href) return null;
                        return (
                          <li key={sub.key}>
                            <LocalLink
                              to={target.href}
                              data-i18n-substitute={target.fellBack ? "" : undefined}
                              aria-current={target.fellBack ? undefined : navAria(target.href)}
                              className="eb-mnav block py-2.5 text-lg text-muted-foreground hover:text-foreground transition-colors font-light"
                            >
                              {sub.label[locale]}
                            </LocalLink>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ) : (
                  <li key={l.key} style={reveal}>
                    <LocalLink
                      to={href}
                      data-i18n-substitute={fellBack ? "" : undefined}
                      aria-current={fellBack ? undefined : navAria(href)}
                      className="eb-mnav block py-3 text-foreground text-2xl md:text-3xl font-light tracking-wide hover:opacity-60 transition-opacity"
                    >
                      {label}
                    </LocalLink>
                  </li>
                );
              })}

              {/* Переключатель языка — той же строкой, что и пункты меню */}
              {twinHref && (
                <li
                  style={{
                    opacity: menuVisible ? 1 : 0,
                    transform: menuVisible ? "translateY(0)" : "translateY(20px)",
                    transition: `opacity 0.4s ease-out ${0.08 + navLinks.length * 0.05}s, transform 0.4s ease-out ${0.08 + navLinks.length * 0.05}s`,
                  }}
                >
                  <Link
                    to={twinHref}
                    hrefLang={otherLocale === "pt" ? "pt-PT" : "en"}
                    lang={otherLocale === "pt" ? "pt-PT" : "en"}
                    data-language-switch={otherLocale}
                    aria-label={LANGUAGE_SWITCH[otherLocale].aria}
                    className="eb-mnav block py-3 text-foreground text-lg font-light tracking-wide opacity-70 hover:opacity-100 transition-opacity"
                  >
                    {otherLocale === "pt" ? "Português" : "English"}
                  </Link>
                </li>
              )}
            </ul>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
